import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, homeFor } from '../context/AuthContext.jsx';
import PublicShell from '../components/PublicShell.jsx';
import CubMascot from '../components/CubMascot.jsx';

/**
 * About, on its own page.
 *
 * Everything here is checkable against the product. No founder story,
 * no school count, no testimonials and no awards - a public page aimed
 * at parents is not somewhere to invent credentials, and anything of
 * that sort has to come from somebody who knows it is true.
 */

const APPROACH = [
  {
    icon: '🖐️',
    title: 'Doing, not being told',
    text:
      'Nobody learns to stay safe online by being told to be careful. Every lesson ends with the child having done something — sorting a real inbox, following a packet across the world, or opening a command prompt and looking around.',
  },
  {
    icon: '🎬',
    title: 'Explained properly',
    text:
      'The harder ideas arrive as short animated explainers: narrated, subtitled, and with the voice and sound both switchable. No wall of text, and nothing that assumes a grown-up is sitting alongside.',
  },
  {
    icon: '🪜',
    title: 'Two courses that join up',
    text:
      'Cyber Smart Kids starts at seven and covers staying safe. Ethical Hacker picks up at eleven and goes underneath — how computers, data and networks actually work, and how attacks really happen.',
  },
  {
    icon: '📱',
    title: 'Nothing to install',
    text:
      'It runs in a browser, on a school laptop or a phone on the sofa. Progress follows the child, so a lesson started in class can be finished at home.',
  },
];

const PRINCIPLES = [
  {
    title: 'Never frightening',
    text:
      'Children who are scared of the internet do not become safer — they become quiet, and a quiet child does not tell anybody when something goes wrong. Lessons end on what to do, not on what might happen.',
  },
  {
    title: 'Telling is never trouble',
    text:
      'The single most useful thing a child can learn is that reporting something is not an admission of having done wrong. It runs through both courses, and the practicals say so out loud.',
  },
  {
    title: 'How things work, not just rules',
    text:
      'A rule is forgotten the moment it is inconvenient. Someone who understands why a padlock matters, or what an open port is, can work out the answer to a situation nobody prepared them for.',
  },
  {
    title: 'Recognise, do not perform',
    text:
      'The Ethical Hacker course teaches how attacks are assembled so they can be spotted and stopped. It carries no instructions for carrying one out, and the practicals are read-only by design.',
  },
];

const NOT_DOING = [
  'No advertising, and nothing sold to your child',
  'No messaging between users — there is nobody for a stranger to be',
  'No public profiles beyond the leaderboard',
  'Accounts hold a name, an age, a parent’s mobile number, and the email used to sign in',
];

export default function About() {
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'About · CyberCub';
  }, []);

  return (
    <PublicShell>
      {/* ---------------- opening ---------------- */}
      <section className="about-head">
        <div className="about-head-text">
          <span className="home-eyebrow">About CyberCub</span>
          <h1>
            Children meet the internet
            <br />
            <span className="hl">years before anybody explains it.</span>
          </h1>
          <p className="home-lede">
            CyberCub is that explanation — built as a course a child will actually finish, rather
            than a talk they will sit through. Two courses, from age seven upwards, made of short
            lessons that each end in something done.
          </p>
        </div>
        <CubMascot avatar="cub-teal" mood="star" size={150} className="anim-float" />
      </section>

      {/* ---------------- approach ---------------- */}
      <section className="home-section" id="approach">
        <h2 className="home-h2">How it is built</h2>
        <div className="home-about">
          {APPROACH.map((a) => (
            <div key={a.title} className="home-about-card">
              <span className="home-about-icon" aria-hidden="true">{a.icon}</span>
              <strong>{a.title}</strong>
              <p>{a.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- principles ---------------- */}
      <section className="home-section home-band" id="principles">
        <h2 className="home-h2">What we believe</h2>
        <p className="home-sub">
          Four decisions that shaped every lesson in both courses.
        </p>
        <div className="about-principles">
          {PRINCIPLES.map((p, i) => (
            <div key={p.title} className="about-principle">
              <span className="about-principle-n" aria-hidden="true">{i + 1}</span>
              <div>
                <strong>{p.title}</strong>
                <p>{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- what we do not do ---------------- */}
      <section className="home-section" id="safety">
        <h2 className="home-h2">For the grown-ups</h2>
        <p className="home-sub">
          A safety course that was careless with a child’s data would not be worth much. What
          CyberCub deliberately does not do:
        </p>
        <ul className="about-not">
          {NOT_DOING.map((n) => <li key={n}>{n}</li>)}
        </ul>
      </section>

      {/* ---------------- close ---------------- */}
      <section className="home-section home-band about-close">
        <h2 className="home-h2">Have a look</h2>
        <p className="home-sub">
          The courses page lists everything in both tracks, lesson by lesson.
        </p>
        <div className="home-cta">
          {user ? (
            <Link to={homeFor(user.role)} className="btn btn-lg">My courses →</Link>
          ) : (
            <>
              <Link to="/login" className="btn btn-lg">Log in</Link>
              <Link to="/#courses" className="btn btn-lg btn-ghost">See the courses</Link>
            </>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
